import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import type {
  ResumeContact,
  ResumeExperience,
  ResumeEducation,
  ResumeSkillGroup,
  ResumeProject,
  ResumeCertification,
  ResumeLanguage,
  ResumeCustomSection,
} from '../../types'
import { resumeId } from '../../utils/resumeBuilder'

const inputClass =
  'w-full rounded-md border border-app-line bg-app-surface px-2.5 py-1.5 text-sm text-app-ink focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent'
const labelClass = 'block text-xs font-medium text-app-muted mb-1'

export function SectionCard({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="border border-app-line bg-app-surface p-4 rounded-2xl shadow-app-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-app-ink font-display">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  )
}

export function IconButton({ onClick, label, children, variant = 'ghost' }: { onClick: () => void; label: string; children: React.ReactNode; variant?: 'ghost' | 'primary' | 'danger' }) {
  const cls =
    variant === 'primary' ? 'bg-app-primary text-white hover:bg-app-primary-hover' :
    variant === 'danger' ? 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20' :
    'text-app-muted hover:bg-app-subtle'
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors ${cls}`}>
      {children}
    </button>
  )
}

function AddItemButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <IconButton onClick={onClick} label={label} variant="primary">
      <PlusIcon className="h-3.5 w-3.5" /> {label}
    </IconButton>
  )
}

// =========================
// Contact + Summary
// =========================

export function ContactSection({ value, onChange }: { value: ResumeContact; onChange: (v: ResumeContact) => void }) {
  const set = (k: keyof ResumeContact, v: string) => onChange({ ...value, [k]: v })
  return (
    <SectionCard title="Contact">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelClass}>Full name</label><input className={inputClass} value={value.fullName ?? ''} onChange={(e) => set('fullName', e.target.value)} /></div>
        <div><label className={labelClass}>Headline / target role</label><input className={inputClass} value={value.headline ?? ''} onChange={(e) => set('headline', e.target.value)} /></div>
        <div><label className={labelClass}>Email</label><input className={inputClass} value={value.email ?? ''} onChange={(e) => set('email', e.target.value)} /></div>
        <div><label className={labelClass}>Phone</label><input className={inputClass} value={value.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></div>
        <div><label className={labelClass}>Location</label><input className={inputClass} value={value.location ?? ''} onChange={(e) => set('location', e.target.value)} /></div>
        <div><label className={labelClass}>Website</label><input className={inputClass} value={value.website ?? ''} onChange={(e) => set('website', e.target.value)} /></div>
        <div><label className={labelClass}>LinkedIn</label><input className={inputClass} value={value.linkedin ?? ''} onChange={(e) => set('linkedin', e.target.value)} /></div>
        <div><label className={labelClass}>GitHub</label><input className={inputClass} value={value.github ?? ''} onChange={(e) => set('github', e.target.value)} /></div>
      </div>
    </SectionCard>
  )
}

export function SummarySection({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  return (
    <SectionCard title="Summary">
      <textarea
        className={inputClass + ' min-h-[100px]'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="2-4 sentences positioning you for the role."
      />
    </SectionCard>
  )
}

// =========================
// Experience
// =========================

export function ExperienceSection({ value, onChange }: { value: ResumeExperience[]; onChange: (v: ResumeExperience[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), company: '', role: '', bullets: [''] }])
  const update = (id: string, patch: Partial<ResumeExperience>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Experience" action={<AddItemButton onClick={add} label="Add role" />}>
      <div className="space-y-3">
        {value.map((exp) => (
          <div key={exp.id} className="rounded-md border border-app-line p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Role</label><input className={inputClass} value={exp.role} onChange={(e) => update(exp.id, { role: e.target.value })} /></div>
              <div><label className={labelClass}>Company</label><input className={inputClass} value={exp.company} onChange={(e) => update(exp.id, { company: e.target.value })} /></div>
              <div><label className={labelClass}>Location</label><input className={inputClass} value={exp.location ?? ''} onChange={(e) => update(exp.id, { location: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className={labelClass}>Start</label><input className={inputClass} value={exp.startDate ?? ''} onChange={(e) => update(exp.id, { startDate: e.target.value })} placeholder="Jan 2023" /></div>
                <div><label className={labelClass}>End</label><input className={inputClass} value={exp.endDate ?? ''} onChange={(e) => update(exp.id, { endDate: e.target.value })} placeholder="Present" /></div>
              </div>
            </div>
            <BulletsEditor value={exp.bullets} onChange={(bullets) => update(exp.id, { bullets })} />
            <div className="flex justify-end">
              <IconButton onClick={() => remove(exp.id)} label="Remove role" variant="danger"><TrashIcon className="h-3.5 w-3.5" /> Remove</IconButton>
            </div>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No experience yet. Click "Add role" to start.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

function BulletsEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const update = (i: number, v: string) => onChange(value.map((b, idx) => idx === i ? v : b))
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i))
  const add = () => onChange([...value, ''])
  return (
    <div>
      <label className={labelClass}>Bullets</label>
      <div className="space-y-1.5">
        {value.map((b, i) => (
          <div key={i} className="flex gap-2">
            <textarea className={inputClass + ' min-h-[36px] resize-y'} value={b} onChange={(e) => update(i, e.target.value)} placeholder="Achievement with measurable impact" />
            <IconButton onClick={() => remove(i)} label="Remove bullet" variant="danger"><TrashIcon className="h-3.5 w-3.5" /></IconButton>
          </div>
        ))}
        <button type="button" onClick={add} className="text-xs text-app-accent hover:underline">+ Add bullet</button>
      </div>
    </div>
  )
}

// =========================
// Education
// =========================

export function EducationSection({ value, onChange }: { value: ResumeEducation[]; onChange: (v: ResumeEducation[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), institution: '' }])
  const update = (id: string, patch: Partial<ResumeEducation>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Education" action={<AddItemButton onClick={add} label="Add education" />}>
      <div className="space-y-3">
        {value.map((e) => (
          <div key={e.id} className="rounded-md border border-app-line p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Institution</label><input className={inputClass} value={e.institution} onChange={(ev) => update(e.id, { institution: ev.target.value })} /></div>
              <div><label className={labelClass}>Location</label><input className={inputClass} value={e.location ?? ''} onChange={(ev) => update(e.id, { location: ev.target.value })} /></div>
              <div><label className={labelClass}>Degree</label><input className={inputClass} value={e.degree ?? ''} onChange={(ev) => update(e.id, { degree: ev.target.value })} /></div>
              <div><label className={labelClass}>Field</label><input className={inputClass} value={e.field ?? ''} onChange={(ev) => update(e.id, { field: ev.target.value })} /></div>
              <div><label className={labelClass}>Start</label><input className={inputClass} value={e.startDate ?? ''} onChange={(ev) => update(e.id, { startDate: ev.target.value })} /></div>
              <div><label className={labelClass}>End</label><input className={inputClass} value={e.endDate ?? ''} onChange={(ev) => update(e.id, { endDate: ev.target.value })} /></div>
            </div>
            <div><label className={labelClass}>Details</label><textarea className={inputClass + ' min-h-[48px]'} value={e.details ?? ''} onChange={(ev) => update(e.id, { details: ev.target.value })} /></div>
            <div className="flex justify-end">
              <IconButton onClick={() => remove(e.id)} label="Remove education" variant="danger"><TrashIcon className="h-3.5 w-3.5" /> Remove</IconButton>
            </div>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No education entries yet.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

// =========================
// Skills
// =========================

export function SkillsSection({ value, onChange }: { value: ResumeSkillGroup[]; onChange: (v: ResumeSkillGroup[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), category: '', items: [] }])
  const update = (id: string, patch: Partial<ResumeSkillGroup>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Skills" action={<AddItemButton onClick={add} label="Add group" />}>
      <div className="space-y-2">
        {value.map((g) => (
          <div key={g.id} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end">
            <div><label className={labelClass}>Category</label><input className={inputClass} value={g.category} onChange={(e) => update(g.id, { category: e.target.value })} placeholder="Languages" /></div>
            <div>
              <label className={labelClass}>Items (comma separated)</label>
              <input className={inputClass} value={g.items.join(', ')} onChange={(e) => update(g.id, { items: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
            </div>
            <IconButton onClick={() => remove(g.id)} label="Remove group" variant="danger"><TrashIcon className="h-3.5 w-3.5" /></IconButton>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No skill groups yet.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

// =========================
// Projects
// =========================

export function ProjectsSection({ value, onChange }: { value: ResumeProject[]; onChange: (v: ResumeProject[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), name: '', bullets: [] }])
  const update = (id: string, patch: Partial<ResumeProject>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Projects" action={<AddItemButton onClick={add} label="Add project" />}>
      <div className="space-y-3">
        {value.map((p) => (
          <div key={p.id} className="rounded-md border border-app-line p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Name</label><input className={inputClass} value={p.name} onChange={(e) => update(p.id, { name: e.target.value })} /></div>
              <div><label className={labelClass}>URL</label><input className={inputClass} value={p.url ?? ''} onChange={(e) => update(p.id, { url: e.target.value })} /></div>
            </div>
            <div><label className={labelClass}>Description</label><textarea className={inputClass + ' min-h-[48px]'} value={p.description ?? ''} onChange={(e) => update(p.id, { description: e.target.value })} /></div>
            <BulletsEditor value={p.bullets} onChange={(bullets) => update(p.id, { bullets })} />
            <div className="flex justify-end">
              <IconButton onClick={() => remove(p.id)} label="Remove project" variant="danger"><TrashIcon className="h-3.5 w-3.5" /> Remove</IconButton>
            </div>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No projects yet.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

// =========================
// Certifications
// =========================

export function CertificationsSection({ value, onChange }: { value: ResumeCertification[]; onChange: (v: ResumeCertification[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), name: '' }])
  const update = (id: string, patch: Partial<ResumeCertification>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Certifications" action={<AddItemButton onClick={add} label="Add cert" />}>
      <div className="space-y-2">
        {value.map((c) => (
          <div key={c.id} className="grid grid-cols-[2fr_2fr_1fr_auto] gap-2 items-end">
            <div><label className={labelClass}>Name</label><input className={inputClass} value={c.name} onChange={(e) => update(c.id, { name: e.target.value })} /></div>
            <div><label className={labelClass}>Issuer</label><input className={inputClass} value={c.issuer ?? ''} onChange={(e) => update(c.id, { issuer: e.target.value })} /></div>
            <div><label className={labelClass}>Date</label><input className={inputClass} value={c.date ?? ''} onChange={(e) => update(c.id, { date: e.target.value })} /></div>
            <IconButton onClick={() => remove(c.id)} label="Remove cert" variant="danger"><TrashIcon className="h-3.5 w-3.5" /></IconButton>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No certifications yet.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

// =========================
// Languages
// =========================

export function LanguagesSection({ value, onChange }: { value: ResumeLanguage[]; onChange: (v: ResumeLanguage[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), name: '' }])
  const update = (id: string, patch: Partial<ResumeLanguage>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Languages" action={<AddItemButton onClick={add} label="Add language" />}>
      <div className="space-y-2">
        {value.map((l) => (
          <div key={l.id} className="grid grid-cols-[2fr_2fr_auto] gap-2 items-end">
            <div><label className={labelClass}>Language</label><input className={inputClass} value={l.name} onChange={(e) => update(l.id, { name: e.target.value })} /></div>
            <div><label className={labelClass}>Proficiency</label><input className={inputClass} value={l.proficiency ?? ''} onChange={(e) => update(l.id, { proficiency: e.target.value })} placeholder="C1 / Native" /></div>
            <IconButton onClick={() => remove(l.id)} label="Remove language" variant="danger"><TrashIcon className="h-3.5 w-3.5" /></IconButton>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No languages yet.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

// =========================
// Custom sections
// =========================

export function CustomSectionsSection({ value, onChange }: { value: ResumeCustomSection[]; onChange: (v: ResumeCustomSection[]) => void }) {
  const add = () => onChange([...value, { id: resumeId(), title: 'New section', body: '' }])
  const update = (id: string, patch: Partial<ResumeCustomSection>) => onChange(value.map(it => it.id === id ? { ...it, ...patch } : it))
  const remove = (id: string) => onChange(value.filter(it => it.id !== id))
  return (
    <SectionCard title="Custom sections" action={<AddItemButton onClick={add} label="Add section" />}>
      <div className="space-y-3">
        {value.map((s) => (
          <div key={s.id} className="rounded-md border border-app-line p-3 space-y-2">
            <div><label className={labelClass}>Title</label><input className={inputClass} value={s.title} onChange={(e) => update(s.id, { title: e.target.value })} /></div>
            <div><label className={labelClass}>Body</label><textarea className={inputClass + ' min-h-[80px]'} value={s.body} onChange={(e) => update(s.id, { body: e.target.value })} /></div>
            <div className="flex justify-end">
              <IconButton onClick={() => remove(s.id)} label="Remove section" variant="danger"><TrashIcon className="h-3.5 w-3.5" /> Remove</IconButton>
            </div>
          </div>
        ))}
        {value.length === 0 && <EmptyHint>No custom sections.</EmptyHint>}
      </div>
    </SectionCard>
  )
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-app-muted italic">{children}</p>
}
